// src/pages/privacy-policy/page.tsx

import React from "react";
// Import Link for navigation in the Quick Links section (if added later)
import { Link } from "react-router-dom";

export default function PrivacyPolicy(): React.ReactElement {
    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f0fafa] via-[#e0f2f2] to-[#d0e8e8] pt-28 md:pt-36 pb-12 px-4">
            <div className="max-w-4xl mx-auto animate-fadeIn">
                {/* Header */}
                <div className="text-center mb-12">
                    <span className="inline-block px-3 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full bg-gradient-to-r from-[#174143] to-[#3d9ea4] text-white mb-3 shadow-md">
                        Legal
                    </span>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-gray-600">Last updated: 04/10/2025</p>
                </div>

                {/* NOTE: Quick Links section usually belongs here, using Link/a tags as needed. Not included as it wasn't in this specific upload. */}

                {/* Content Card */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 p-6 md:p-10 space-y-8">
                    <section>
                        <h2 className="text-xl font-bold text-[#174143] mb-4 flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-[#3d9ea4] rounded-full"></span>
                            Privacy Policy — Required Summary
                        </h2>
                        <p className="text-gray-600 leading-relaxed">
                            This policy describes the data collected and how it is used for the Vidyasamrudhi mobile application.
                        </p>
                    </section>

                    <div className="grid md:grid-cols-2 gap-8">
                        <section className="bg-white/50 rounded-xl p-6 border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-[#174143]/10 flex items-center justify-center text-[#174143] text-sm">01</span>
                                Data we collect
                            </h3>
                            <ul className="space-y-3 text-gray-600 text-sm">
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span><strong>Personal data:</strong> Name, Email — for account creation and authentication.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span><strong>Profile photo:</strong> Uploaded or captured by the user via Camera or selected from the device gallery.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span><strong>Device & diagnostic data:</strong> Device model, OS version, app usage events, crash logs.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span><strong>Biometric:</strong> Local authentication only via Android Biometric APIs.</span>
                                </li>
                            </ul>
                        </section>

                        <section className="bg-white/50 rounded-xl p-6 border border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <span className="w-8 h-8 rounded-lg bg-[#174143]/10 flex items-center justify-center text-[#174143] text-sm">02</span>
                                Purpose of collection
                            </h3>
                            <ul className="space-y-3 text-gray-600 text-sm">
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Authentication and account management (sign-in, recovery).</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>App functionality (profile management, offline downloads).</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Improving app performance and stability.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-[#3d9ea4] mt-1">•</span>
                                    <span>Customer support and important service messages.</span>
                                </li>
                            </ul>
                        </section>
                    </div>

                    <section>
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <span className="w-8 h-8 rounded-lg bg-[#174143]/10 flex items-center justify-center text-[#174143] text-sm">03</span>
                            Permissions requested
                        </h3>
                        <div className="bg-blue-50/50 border-l-4 border-[#3d9ea4] p-6 rounded-r-xl">
                            <ul className="grid sm:grid-cols-2 gap-4 text-gray-700 text-sm">
                                <li className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-[#3d9ea4] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span><strong>USE_BIOMETRIC:</strong> Local device authentication only.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-[#3d9ea4] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span><strong>CAMERA:</strong> Capture profile images.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-[#3d9ea4] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span><strong>READ_MEDIA_IMAGES:</strong> Select profile images.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-[#3d9ea4] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span><strong>INTERNET:</strong> Server communication.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-[#3d9ea4] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span><strong>POST_NOTIFICATIONS:</strong> App notifications.</span>
                                </li>
                            </ul>
                        </div>
                    </section>

                    <div className="grid md:grid-cols-2 gap-8">
                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">4. Third-party services</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                We use trusted third-party providers (e.g., Firebase Analytics, Crashlytics) for analytics and crash reporting. These providers receive limited diagnostic data under their own privacy policies. We do not sell personal data to third parties.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">5. Data storage & security</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Personal data is retained only as long as necessary. Sensitive data is encrypted. Biometric verification is performed locally; we never transmit raw biometric templates to our servers.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">6. Children</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                The app is intended for educational use. Children under 13 should use the app with parental supervision. We do not knowingly collect personal data from children without parental consent.
                            </p>
                        </section>

                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-3">7. User rights</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Users may request access, correction, or deletion of their personal data. Verified deletion requests will be processed in accordance with applicable law and our retention policies.
                            </p>
                        </section>
                    </div>

                    <section className="bg-[#174143]/5 rounded-xl p-6 text-center">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">8. Contact & Updates</h3>
                        <p className="text-gray-600 text-sm mb-4">
                            For privacy questions or to submit data requests contact us. We may update this policy periodically.
                        </p>
                        <a href="mailto:vidyasamruddhi2025@gmail.com" className="inline-flex items-center gap-2 px-6 py-2 bg-[#174143] text-white rounded-full font-semibold hover:bg-[#1f5961] transition-colors shadow-lg hover:shadow-xl">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            vidyasamruddhi2025@gmail.com
                        </a>
                    </section>

                    <section>
                        <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">10. Exact manifest permissions</h3>
                        <div className="bg-[#0f172a] rounded-xl p-4 shadow-inner overflow-hidden">
                            <pre className="text-gray-300 text-xs md:text-sm font-mono overflow-x-auto">
                                {`<uses-permission android:name="android.permission.USE_BIOMETRIC" />
<uses-permission android:name="android.permission.USE_FINGERPRINT" />
<uses-permission android:name="android.permission.CAMERA" />
<uses-permission android:name="android.permission.READ_MEDIA_IMAGES" />
<uses-permission android:name="android.permission.READ_MEDIA_VIDEO" />
<uses-permission android:name="android.permission.INTERNET" />
<uses-permission android:name="android.permission.POST_NOTIFICATIONS" />`}
                            </pre>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}